import React from 'react';
import { Download, FileText, FolderOpen, Clock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumbs from '../components/seo/Breadcrumbs';

const Downloads = () => {
  const downloadCategories = [
    {
      category: 'Product Brochures',
      icon: FileText,
      files: [
        { name: 'Complete Product Range Brochure', url: '/downloads/alden-product-brochure.pdf' },
        { name: '30 BPM Water Filling Machine', status: 'coming' },
        { name: '60 BPM Water Filling Machine', status: 'coming' },
        { name: '120 BPM Water Filling Machine', status: 'coming' },
        { name: '200 BPM Water Filling Machine', status: 'coming' },
        { name: 'RO Water Plants', status: 'coming' },
        { name: 'PET Bottle Blowing Machine', status: 'coming' }
      ]
    },
    {
      category: 'Technical Datasheets',
      icon: FolderOpen,
      files: [
        { name: 'Filling Machine Specifications', status: 'coming' },
        { name: 'RO Plant Technical Data', status: 'coming' },
        { name: 'Electrical Requirements', status: 'coming' },
        { name: 'Installation Requirements', status: 'coming' }
      ]
    },
    {
      category: 'Company Documents',
      icon: FileText,
      files: [
        { name: 'Company Profile', url: '/downloads/alden-company-profile.pdf' },
        { name: 'Product Catalogue 2025', url: '/downloads/alden-product-brochure.pdf' },
        { name: 'ISO Certificates', status: 'coming' },
        { name: 'BIS Certification', status: 'coming' }
      ]
    },
    {
      category: 'Operation & Maintenance',
      icon: FolderOpen,
      files: [
        { name: 'Installation Manual', status: 'coming' },
        { name: 'Operation Manual', status: 'coming' },
        { name: 'Preventive Maintenance Checklist', status: 'coming' },
        { name: 'Troubleshooting Guide', status: 'coming' }
      ]
    },
    {
      category: 'Policies & Terms',
      icon: FileText,
      files: [
        { name: 'Warranty Policy', status: 'coming' },
        { name: 'AMC Brochure', status: 'coming' },
        { name: 'Terms & Conditions', status: 'coming' },
        { name: 'Payment Terms', status: 'coming' }
      ]
    }
  ];

  return (
    <div className="min-h-screen pb-20 lg:pb-0">
      <SEOHead
        title="Downloads — Brochures & Machine Catalogues | Alden Technologies"
        description="Download product brochures, technical catalogues and project guides for water filling machines, RO plants and turnkey mineral water plant solutions."
        path="/downloads"
      />
      <Breadcrumbs items={[{ label: 'Downloads' }]} />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-alden-green-dark via-alden-green to-alden-green-light text-white py-20 md:py-28">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-block bg-white/20 px-6 py-3 rounded-full text-sm font-semibold mb-6">
            📥 Resource Center
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Downloads
          </h1>
          <p className="text-lg sm:text-xl max-w-3xl mx-auto text-white/90 leading-relaxed">
            Access product brochures, technical datasheets, operation manuals, and company documents.
          </p>
        </div>
      </section>

      {/* Downloads Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="space-y-12">
            {downloadCategories.map((category) => (
              <div key={category.category}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-alden-green/10 rounded-lg flex items-center justify-center">
                    <category.icon className="w-6 h-6 text-alden-green" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">{category.category}</h2>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {category.files.map((file) => (
                    <Card key={`${category.category}-${file.name}`} className="border-2 border-alden-green/20 hover:border-alden-green transition-all">
                      <CardContent className="p-6">
                        <FileText className="w-10 h-10 text-alden-green mb-4" />
                        <h3 className="font-bold text-gray-900 mb-3 line-clamp-2">{file.name}</h3>
                        {file.status === 'coming' ? (
                          <div className="flex items-center gap-2 text-gray-500">
                            <Clock className="w-4 h-4" />
                            <span className="text-sm font-semibold">Coming Soon</span>
                          </div>
                        ) : (
                          <a href={file.url} download data-testid={`download-file-${file.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                            <Button className="w-full bg-alden-green hover:bg-alden-green-light text-white">
                              <Download className="w-4 h-4 mr-2" />
                              Download
                            </Button>
                          </a>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Need Custom Documents */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Need Custom Documentation?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Contact us if you need specific technical documents, datasheets, or customized information.
          </p>
          <Button className="bg-alden-green hover:bg-alden-green-light text-white py-6 px-10 text-lg font-semibold">
            Contact Us
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Downloads;